'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { Modal } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import styles from '../ktechpioneers.module.scss'

interface PioneersModalProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  children: React.ReactNode
}

const PioneersModal = ({ isOpen, onClose, title, children }: PioneersModalProps) => {
  const isMobile = useMediaQuery('(max-width: 1279px)')
  const scrollRef = useRef<HTMLUListElement>(null)
  const [thumbHeight, setThumbHeight] = useState(0)
  const [thumbTop, setThumbTop] = useState(0)
  const [needsScroll, setNeedsScroll] = useState(false)

  const updateScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const hasOverflow = el.scrollHeight > el.clientHeight
    setNeedsScroll(hasOverflow)
    if (hasOverflow) {
      const ratio = el.clientHeight / el.scrollHeight
      setThumbHeight(Math.max(ratio * el.clientHeight, 40))
      setThumbTop((el.scrollTop / el.scrollHeight) * el.clientHeight)
    }
  }, [])

  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(updateScroll)
    } else {
      setNeedsScroll(false)
    }
  }, [isOpen, updateScroll])

  return (
    <Modal
      opened={isOpen}
      centered
      onClose={onClose}
      title={
        title && (
          <span
            style={{
              color: '#fff',
              fontSize: isMobile ? '24px' : '46px',
              fontWeight: 700,
            }}>
            {title}
          </span>
        )
      }
      size={isMobile ? 'calc(100vw - 32px)' : '976px'}
      closeButtonProps={{
        icon: isMobile ? (
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <rect width="24" height="24" rx="12" fill="white" fillOpacity="0.2" />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M15.625 7.76318C15.7938 7.59445 16.0675 7.59472 16.2364 7.76318C16.4052 7.93204 16.4052 8.20564 16.2364 8.3745L12.6114 11.9995L16.2364 15.6245C16.4052 15.7933 16.4051 16.067 16.2364 16.2358C16.0675 16.4047 15.7939 16.4047 15.625 16.2358L12 12.6108L8.37503 16.2358C8.20617 16.4047 7.93256 16.4047 7.76371 16.2358C7.59526 16.0669 7.595 15.7932 7.76371 15.6245L11.3887 11.9995L7.76371 8.3745C7.59514 8.20562 7.59494 7.93194 7.76371 7.76318C7.93248 7.5945 8.20618 7.59464 8.37503 7.76318L12 11.3882L15.625 7.76318Z"
              fill="white"
            />
          </svg>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" width="44" height="44" viewBox="0 0 44 44" fill="none">
            <rect width="44" height="44" rx="22" fill="white" fillOpacity="0.2" />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M28.6464 14.232C28.956 13.9227 29.458 13.9226 29.7675 14.232C30.077 14.5415 30.0768 15.0435 29.7675 15.3531L23.121 21.9986L29.7675 28.6451C30.0767 28.9546 30.0768 29.4567 29.7675 29.7662C29.458 30.0757 28.956 30.0755 28.6464 29.7662L21.9999 23.1197L15.3544 29.7662C15.0448 30.0755 14.5428 30.0757 14.2333 29.7662C13.924 29.4567 13.9241 28.9547 14.2333 28.6451L20.8788 21.9986L14.2333 15.3531C13.9239 15.0435 13.9238 14.5415 14.2333 14.232C14.5429 13.9227 15.0449 13.9227 15.3544 14.232L21.9999 20.8775L28.6464 14.232Z"
              fill="white"
            />
          </svg>
        ),
      }}
      styles={{
        close: {
          padding: 0,
          background: 'transparent',
          border: 'none',
          outline: 'none',
          '&:hover': {
            outline: 'none !important',
            background: 'transparent !important',
          },
          '&:focus': {
            outline: 'none !important',
            background: 'transparent !important',
          },
          '& > svg': {
            width: '44px',
            height: '44px',
          },
          width: '44px',
          height: '44px',
        },
        inner: { padding: '0' },
        content: {
          background: '#000',
          boxShadow: 'none',
          borderRadius: '12px',
          maxHeight: isMobile ? '70vh' : 'calc(100vh - 300px)',
          display: 'flex',
          flexDirection: 'column' as const,
        },
        header: {
          padding: title ? (isMobile ? '16px 16px 0' : '64px 64px 0') : '0',
          background: 'transparent',
          marginBottom: 0,
        },
        body: {
          padding: needsScroll
            ? (isMobile ? '16px 6px 16px 16px' : '64px 58px 64px 64px')
            : (isMobile ? '16px' : '64px'),
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column' as const,
          flex: 1,
          minHeight: 0,
        },
        overlay: {
          backdropFilter: 'blur(2px)',
        },
      }}>
      <div
        className={styles.scrollWrapper}
        style={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <ul
          ref={scrollRef}
          onScroll={updateScroll}
          className={`${styles.scrollContent} flex flex-col gap-4 pc:gap-8`}
          style={{
            flex: 1,
            minHeight: 0,
            paddingRight: needsScroll ? (isMobile ? '12px' : '16px') : undefined,
          }}>
          {children}
        </ul>
        {needsScroll && (
          <>
            <div className={styles.scrollTrack} />
            <div className={styles.scrollThumb} style={{ height: thumbHeight, top: thumbTop }} />
          </>
        )}
      </div>
    </Modal>
  )
}

export default PioneersModal
